import React, { useState, useEffect, useCallback } from 'react';
import {
    Container, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Box, TextField, Select, MenuItem, FormControl, InputLabel,
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, Chip, Snackbar, Alert,
    DialogContentText, Pagination, CircularProgress
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Search as SearchIcon, SwapHoriz as SwapHorizIcon } from '@mui/icons-material';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';

const StockTransfers = () => {
    const [stockTransfers, setStockTransfers] = useState([]);
    const [selectedTransfer, setSelectedTransfer] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [availableLocations, setAvailableLocations] = useState([]);
    const [userLocation, setUserLocation] = useState('');
    const [userRole, setUserRole] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [isLoading, setIsLoading] = useState(true);

    // Filters and pagination
    const [productSearch, setProductSearch] = useState('');
    const [fromLocation, setFromLocation] = useState('All');
    const [toLocation, setToLocation] = useState('All');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [entriesPerPage, setEntriesPerPage] = useState(20);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({
        current: 1,
        total: 1,
        hasNext: false,
        hasPrev: false,
        totalRecords: 0,
        limit: 20
    });

    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
            return;
        }

        const storedLocation = localStorage.getItem('userLocation');
        const role = localStorage.getItem('userRole');
        setUserLocation(storedLocation);
        setUserRole(role);

        fetchLocations();
    }, [navigate]);

    const fetchLocations = async () => {
        try {
            const locationsResponse = await api.get('/config/locations');
            setAvailableLocations(locationsResponse.data.storageLocations);
        } catch (error) {
            console.error('Error fetching locations:', error);
        }
    };

    // Fetch stock transfers with pagination and filters
    const fetchStockTransfers = useCallback(async (page = 1) => {
        setIsLoading(true);
        try {
            const params = {
                page,
                limit: entriesPerPage
            };

            // Add filters only if they have values
            if (productSearch && productSearch.trim()) {
                params.productSearch = productSearch.trim();
            }
            if (fromLocation && fromLocation !== 'All') {
                params.fromLocation = fromLocation;
            }
            if (toLocation && toLocation !== 'All') {
                params.toLocation = toLocation;
            }
            if (startDate) {
                params.startDate = startDate;
            }
            if (endDate) {
                params.endDate = endDate;
            }

            const res = await api.get('/stock-transfers/paginated', { params });
            setStockTransfers(res.data.stockTransfers);
            setPagination(res.data.pagination);
            setCurrentPage(page);
        } catch (error) {
            console.error('Error fetching stock transfers:', error);
            setSnackbar({
                open: true,
                message: 'Failed to fetch stock transfers',
                severity: 'error'
            });
        } finally {
            setIsLoading(false);
        }
    }, [entriesPerPage, productSearch, fromLocation, toLocation, startDate, endDate]);

    // Initial load and when entriesPerPage changes
    useEffect(() => {
        if (userRole) {
            fetchStockTransfers(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entriesPerPage, userRole]);

    const handlePageChange = (event, value) => {
        fetchStockTransfers(value);
    };

    const handleSearch = () => {
        fetchStockTransfers(1); // Reset to page 1 when searching
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    const handleRowClick = (transfer) => {
        setSelectedTransfer(transfer);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedTransfer(null);
    };

    const handleDeleteClick = () => {
        setConfirmDeleteOpen(true);
    };

    const handleConfirmDelete = async () => {
        try {
            await api.delete(`/stock-transfers/${selectedTransfer._id}`, {
                data: { confirmed: true }
            });

            setSnackbar({
                open: true,
                message: 'Stock transfer deleted successfully. Stock has been reversed.',
                severity: 'success'
            });

            setConfirmDeleteOpen(false);
            setDialogOpen(false);
            setSelectedTransfer(null);

            // Refresh the current page
            fetchStockTransfers(currentPage);
        } catch (error) {
            console.error('Error deleting stock transfer:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Failed to delete stock transfer',
                severity: 'error'
            });
        }
    };

    const handleCancelDelete = () => {
        setConfirmDeleteOpen(false);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const getTotalQuantity = (products) => {
        return products.reduce((sum, p) => sum + p.quantity, 0).toFixed(2);
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: 'primary.main', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                >
                    <SwapHorizIcon fontSize="large" />
                    Stock Transfers
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Track and manage intra-inventory stock movements between locations
                </Typography>
                <Button
                    variant="contained"
                    color="info"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/create-stock-transfer')}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                    Create Stock Transfer
                </Button>
            </Box>

            {/* Filters Section */}
            <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            label="Search by Product"
                            value={productSearch}
                            onChange={(e) => setProductSearch(e.target.value)}
                            onKeyPress={handleKeyPress}
                            variant="outlined"
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={6} sm={4} md={2}>
                        <TextField
                            fullWidth
                            label="Start Date"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            variant="outlined"
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={6} sm={4} md={2}>
                        <TextField
                            fullWidth
                            label="End Date"
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            variant="outlined"
                            size="small"
                        />
                    </Grid>

                    {(userRole === 'owner' || userLocation === 'Universal') && (
                        <>
                            <Grid item xs={6} sm={4} md={1.5}>
                                <FormControl fullWidth variant="outlined" size="small">
                                    <InputLabel>From</InputLabel>
                                    <Select
                                        value={fromLocation}
                                        onChange={(e) => setFromLocation(e.target.value)}
                                        label="From"
                                    >
                                        <MenuItem value="All">All</MenuItem>
                                        {availableLocations.map((loc) => (
                                            <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={6} sm={4} md={1.5}>
                                <FormControl fullWidth variant="outlined" size="small">
                                    <InputLabel>To</InputLabel>
                                    <Select
                                        value={toLocation}
                                        onChange={(e) => setToLocation(e.target.value)}
                                        label="To"
                                    >
                                        <MenuItem value="All">All</MenuItem>
                                        {availableLocations.map((loc) => (
                                            <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </>
                    )}

                    <Grid item xs={6} sm={4} md={1.5}>
                        <FormControl fullWidth variant="outlined">
                            <TextField
                                select
                                label="Per page"
                                value={entriesPerPage}
                                onChange={(e) => setEntriesPerPage(e.target.value)}
                                variant="outlined"
                                size="small"
                            >
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={20}>20</MenuItem>
                                <MenuItem value={30}>30</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                            </TextField>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={1.5}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            startIcon={<SearchIcon />}
                            onClick={handleSearch}
                            sx={{ height: '40px', textTransform: 'none' }}
                        >
                            Search
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'info.main' }}>
                                    <TableCell><Typography variant="subtitle2" fontWeight="bold" color="white">From Location</Typography></TableCell>
                                    <TableCell><Typography variant="subtitle2" fontWeight="bold" color="white">To Location</Typography></TableCell>
                                    <TableCell><Typography variant="subtitle2" fontWeight="bold" color="white">Transfer Date</Typography></TableCell>
                                    <TableCell><Typography variant="subtitle2" fontWeight="bold" color="white">Products</Typography></TableCell>
                                    <TableCell><Typography variant="subtitle2" fontWeight="bold" color="white">Total Quantity</Typography></TableCell>
                                    <TableCell><Typography variant="subtitle2" fontWeight="bold" color="white">Created By</Typography></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {stockTransfers.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body1" color="text.secondary">
                                                No stock transfers found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    stockTransfers.map((transfer) => (
                                        <TableRow
                                            key={transfer._id}
                                            hover
                                            onClick={() => handleRowClick(transfer)}
                                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                                        >
                                            <TableCell>
                                                <Chip label={transfer.fromLocation} size="small" color="error" variant="outlined" />
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={transfer.toLocation} size="small" color="success" variant="outlined" />
                                            </TableCell>
                                            <TableCell>{formatDate(transfer.transferDate)}</TableCell>
                                            <TableCell>{transfer.products.length}</TableCell>
                                            <TableCell>{getTotalQuantity(transfer.products)}</TableCell>
                                            <TableCell>{transfer.createdBy?.name || 'Unknown'}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    {pagination.total > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Pagination
                                count={pagination.total}
                                page={currentPage}
                                onChange={handlePageChange}
                                color="primary"
                                size="large"
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    )}

                    {/* Summary Info */}
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Showing {stockTransfers.length > 0 ? ((currentPage - 1) * entriesPerPage + 1) : 0} to {Math.min(currentPage * entriesPerPage, pagination.totalRecords)} of {pagination.totalRecords} stock transfers
                        </Typography>
                    </Box>
                </>
            )}

            {/* View Details Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
            >
                {selectedTransfer && (
                    <>
                        <DialogTitle sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
                            Stock Transfer Details
                        </DialogTitle>
                        <DialogContent sx={{ mt: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">From Location</Typography>
                                    <Chip label={selectedTransfer.fromLocation} color="error" sx={{ mt: 0.5 }} />
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">To Location</Typography>
                                    <Chip label={selectedTransfer.toLocation} color="success" sx={{ mt: 0.5 }} />
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Transfer Date</Typography>
                                    <Typography variant="body1" fontWeight="600">{formatDate(selectedTransfer.transferDate)}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Created By</Typography>
                                    <Typography variant="body1" fontWeight="600">{selectedTransfer.createdBy?.name || 'Unknown'}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Created At</Typography>
                                    <Typography variant="body1" fontWeight="600">{formatDate(selectedTransfer.createdAt)}</Typography>
                                </Grid>
                                {selectedTransfer.notes && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">Notes</Typography>
                                        <Typography variant="body1">{selectedTransfer.notes}</Typography>
                                    </Grid>
                                )}
                            </Grid>

                            <Typography variant="h6" sx={{ mt: 3, mb: 2, fontWeight: 600 }}>
                                Products Transferred
                            </Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                                            <TableCell><Typography variant="subtitle2" fontWeight="bold">Product</Typography></TableCell>
                                            <TableCell align="right"><Typography variant="subtitle2" fontWeight="bold">Quantity</Typography></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {selectedTransfer.products.map((product, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{product.productName}</TableCell>
                                                <TableCell align="right">{product.quantity}</TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                                            <TableCell align="right"><Typography fontWeight="bold">Total Quantity:</Typography></TableCell>
                                            <TableCell align="right">
                                                <Typography fontWeight="bold">
                                                    {getTotalQuantity(selectedTransfer.products)}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </DialogContent>
                        <DialogActions>
                            {(userRole === 'owner' || userRole === 'employee') && (
                                <Button
                                    onClick={handleDeleteClick}
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Delete Transfer
                                </Button>
                            )}
                            <Button onClick={handleCloseDialog} sx={{ textTransform: 'none' }}>Close</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={confirmDeleteOpen}
                onClose={handleCancelDelete}
            >
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this stock transfer? This will reverse the stock movement - stock will be added back to the source location and removed from the destination. This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDelete} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained" sx={{ textTransform: 'none' }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default StockTransfers;
